import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { Observable, of, forkJoin } from 'rxjs';
import { map, shareReplay, catchError } from 'rxjs/operators';
import { AppLog } from '../log/app-log.service';
import { LogPriority } from '../log/log-priority.enum';
import { merge, set } from 'lodash';

const packageJson = require('../../../../../package.json');

@Injectable({
    providedIn: 'root',
})
export class EnvLoaderService {
    private http = inject(HttpClient);
    private logger = inject(AppLog);

    private readonly configPath = 'assets/config/config.json';
    private readonly secretsPath = 'assets/config/secrets/secrets.json';

    /**
     * Loads configuration and secrets, merges them, and sets them on the environment object.
     *
     * @returns Observable<boolean> - Emits true if loading and merging succeed, false otherwise.
     */
    load(): Observable<boolean> {
        return forkJoin([this.getConfigs(), this.getSecrets()]).pipe(
            map(([config, secrets]) => {
                const dynamicEnv = merge(
                    {},
                    { logPriorityCutoff: LogPriority.MEDIUM, appVersion: packageJson.version },
                    config,
                    this.expandDotNotation(secrets),
                );
                Object.keys(dynamicEnv).forEach((key) => (environment[key] = dynamicEnv[key]));
                return true;
            }),
            catchError(() => of(false)),
        );
    }

    /**
     * Fetches the main configuration JSON file.
     *
     * @returns Observable<any> - Emits the configuration object, or an empty object on error.
     */
    private getConfigs(): Observable<any> {
        return this.http.get(this.configPath, { responseType: 'json' }).pipe(
            shareReplay(1),
            catchError((error) => {
                this.logger.error('Error loading config', { logPriority: LogPriority.HIGH }, error);
                return of({});
            }),
        );
    }

    /**
     * Fetches the secrets JSON file.
     *
     * @returns Observable<any> - Emits the secrets object, or an empty object on error.
     */
    private getSecrets(): Observable<any> {
        return this.http.get(this.secretsPath, { responseType: 'json' }).pipe(
            shareReplay(1),
            catchError((error) => {
                this.logger.error('Error loading secrets', { logPriority: LogPriority.HIGH }, error);
                return of({});
            }),
        );
    }

    /**
     * Expands an object with dot-notation keys into a nested object structure.
     *
     * @param obj - The object with dot-notation keys.
     * @returns A new object with expanded nested properties.
     */
    private expandDotNotation(obj: any): any {
        const result = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                set(result, key, obj[key]);
            }
        }
        return result;
    }
}
